/**
 * ADR-180 — read-only view of a scheduled successor contract.
 *
 * Opened by View on the Successor row of the contract's "What happens next"
 * list (Contracts tab). The successor does not exist until the switch runs;
 * what is shown is the server's projection of the stored replacement,
 * resolved the way the switch will resolve it, with where each value comes
 * from and anything that would make the switch fail or surprise.
 *
 * No inputs. Anyone who can read the person can open it (ADR-180 §8 q4).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material';
import { employmentApi } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import { successorPreviewView } from '../../../constants/successorPreview';

const Row = ({ row }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '180px minmax(0, 1fr)' },
      columnGap: 2,
      rowGap: 0.25,
      py: 0.75
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{row.label}</Typography>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="span"
        sx={{ fontSize: 13, color: row.missing ? '#B91C1C' : '#1e293b', overflowWrap: 'anywhere' }}
      >
        {row.value}
      </Typography>
      {row.note && (
        <Typography sx={{ fontSize: 12, color: '#64748B', mt: '2px' }}>{row.note}</Typography>
      )}
    </Box>
  </Box>
);

const Section = ({ section, isFirst }) => (
  <Box sx={{ ...(isFirst ? {} : { borderTop: '1px solid #E2E8F0', pt: 1.5 }) }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#64748B', mb: 0.5 }}>
      {section.title}
    </Typography>
    {section.note && (
      <Typography sx={{ fontSize: 13, color: '#475569' }}>{section.note}</Typography>
    )}
    {(section.rows || []).map((row) => <Row key={row.label} row={row} />)}
  </Box>
);

const SuccessorPreviewDialog = ({ open, onClose, episodeId, eventId, contractLabel = null, canAct = false }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !episodeId || !eventId) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    employmentApi.getRenewalPreview(episodeId, eventId)
      .then((result) => {
        if (!cancelled) {
          setPreview(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load the scheduled replacement');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, episodeId, eventId]);

  const view = successorPreviewView(preview);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {view?.title || 'Successor contract'}
        {(view?.subtitle || contractLabel) && (
          <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.5 }}>
            {[view?.subtitle, contractLabel ? `Replaces ${contractLabel}` : null].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
        {loading && <LoadingSpinner message="Loading the scheduled replacement..." />}
        {error && <Alert severity="error">{error}</Alert>}
        {view && (
          <>
            {view.warnings.map((warning) => (
              <Alert key={`${warning.code}-${warning.field}-${warning.amendmentEventId || ''}`} severity="warning">
                {warning.message}
              </Alert>
            ))}
            {view.sections.map((section, index) => (
              <Section key={section.title} section={section} isFirst={index === 0} />
            ))}
            {canAct && (
              <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                To change it, cancel the renewal and use Replace Contract again.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessorPreviewDialog;
